<template>
  <div v-if="isStorefront" class="storefront-app"><router-view /></div>
  <div v-else class="app" :class="{'mosaic-page': currentRoute == '/chat', 'attendants-day-app': currentRoute === '/attendants-day'}">
    <div v-if="billingRestrictionMessage" class="billing-restriction-modal" role="dialog" aria-modal="true" aria-labelledby="billingRestrictionTitle">
      <div class="billing-restriction-modal__backdrop" @click="dismissBillingRestrictionMessage"></div>
      <div class="billing-restriction-modal__content">
        <button
          type="button"
          class="billing-restriction-modal__close"
          aria-label="Fechar"
          @click="dismissBillingRestrictionMessage"
        >
          <i class="flaticon-close"></i>
        </button>

        <div class="billing-restriction-modal__icon">
          <i class="flaticon-warning"></i>
        </div>

        <h5 id="billingRestrictionTitle" class="billing-restriction-modal__title">
          Cadastro inativo
        </h5>

        <p class="billing-restriction-modal__message">
          {{ billingRestrictionMessage }}
        </p>

        <button
          type="button"
          class="default-btn position-relative transition border-0 fw-medium text-white pt-11 pb-11 ps-25 pe-25 rounded-1 bg-success fs-md-15 fs-lg-16 w-100"
          @click="dismissBillingRestrictionMessage"
        >
          Entendi
        </button>
      </div>
    </div>

    <div v-if="!loading">
      <MainHeader v-if="isLogged && currentRoute !== '/onboarding' && currentRoute !== '/' && currentRoute !== '/chat' && currentRoute !== '/attendants-day'" />
      <MainSidebar v-if="isLogged && currentRoute !== '/onboarding' && currentRoute !== '/' && !mobileChatOpen" />
      <LogoutModal v-if="isLogged && currentRoute !== '/'" />
      <div class="main-content d-flex flex-column transition overflow-hidden custom-chat-page-style" :class="{'center-main-content': !isLogged || currentRoute === '/onboarding', 'justify-content-start validate-home-page': currentRoute === '/', 'attendants-day-main': currentRoute === '/attendants-day', 'chat-main': currentRoute === '/chat', 'mobile-chat-open': mobileChatOpen, 'with-mobile-bottom-nav': isLogged && currentRoute !== '/' && currentRoute !== '/onboarding', 'onboarding-layout': currentRoute === '/onboarding'}">
        <RegistrationNotice v-if="isLogged && currentRoute !== '/' && currentRoute !== '/onboarding'" />
        <router-view />
        <MainFooter v-if="isLogged && currentRoute !== '/onboarding' && currentRoute !== '/' && currentRoute !== '/chat' && currentRoute !== '/attendants-day'" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, watchEffect } from "vue";
import { mapActions, mapGetters } from 'vuex';
import { useRoute } from 'vue-router';
import stateStore from "./utils/store";

import { isAuthenticated } from './services/authService';
import { ACCESS_PAGES, canAccessPage, PUBLIC_CONTENT_PATHS, PUBLIC_PATHS } from './utils/accessControl';

import MainHeader from "./components/Layouts/MainHeader.vue";
import MainSidebar from "./components/Layouts/MainSidebar.vue";
import MainFooter from "./components/Layouts/MainFooter.vue";
import LogoutModal from "./components/Authentication/LogoutModal.vue";
import RegistrationNotice from './components/Onboarding/RegistrationNotice.vue';
import { landingForUser } from '@/services/registrationExperience';
import { isStorefrontPath } from '@/utils/catalogRoutes';

export default defineComponent({
  name: "App",
  data () {
    return {
      isLogged: false,
      loading: true,
      mobileChatOpen: false,
      billingRestrictionMessage: sessionStorage.getItem("billingRestrictionMessage") || ""
    }
  },
  components: {
    MainHeader,
    MainSidebar,
    MainFooter,
    LogoutModal,
    RegistrationNotice,
  },
  methods: {
    async initializeSession() {
      this.loading = true;
      if (await isAuthenticated()) {
        await this.setAuthenticatedUser();
        await this.setAccessControl();
        if (PUBLIC_PATHS.includes(window.location.pathname) && !PUBLIC_CONTENT_PATHS.includes(window.location.pathname)) {
          window.location.href = landingForUser({ allowed_pages: this.getAllowedPages, onboarding: this.getOnboarding });
          return;
        }
        this.isLogged = true;
      } else {
        this.isLogged = false;
        if (!PUBLIC_PATHS.includes(window.location.pathname) && !isStorefrontPath(window.location.pathname)) window.location.href = '/login';
      }
      this.loading = false;
    },
    ...mapActions([
      "setAuthenticatedUser",
      "setAccessControl",
      "setConfiguration"
    ]),
    onMobileChatOpenChange(event: Event) {
      const customEvent = event as CustomEvent;
      this.mobileChatOpen = Boolean(customEvent.detail?.open);
    },
    updateBillingRestrictionMessage(event: Event) {
      const customEvent = event as CustomEvent;
      this.billingRestrictionMessage = customEvent.detail || "";
    },
    dismissBillingRestrictionMessage() {
      this.billingRestrictionMessage = "";
      sessionStorage.removeItem("billingRestrictionMessage");
    },
    syncDarkMode() {
      const storedDarkMode = localStorage.getItem("dark");
      document.body.classList.toggle("dark", !this.isStorefront && !isStorefrontPath(window.location.pathname) && storedDarkMode === "true");
    }
  },
  computed: {
    ...mapGetters([
      'getPermissions',
      'getOnboarding',
      'getAllowedPages'
    ]),
  },
  watch:{
    currentRoute(newRoute, oldRoute){
      if(!this.isStorefront && !this.loading && this.isLogged && newRoute !== oldRoute){
        this.setConfiguration();
        this.syncDarkMode();
      }
    },
    isStorefront(value) {
      document.body.classList.toggle('bg-body-secondary', !value);
      this.syncDarkMode();
      if (!value) { this.initializeSession(); this.setConfiguration(); }
    },
    getAllowedPages(data){
      const routeAccessKey = (this.$route.meta && this.$route.meta.accessKey) as string | undefined;
      if(routeAccessKey && !canAccessPage(data, routeAccessKey)){
        const page = ACCESS_PAGES.find((item) => item.key === routeAccessKey);
        this.$router.replace({
          path: "/access-denied",
          query: {
            module: page?.label || "este modulo",
            key: routeAccessKey,
          },
        });
      }
    }
  },
  async created() {
    if (isStorefrontPath(window.location.pathname)) { this.loading = false; return; }
    await this.initializeSession();
  },
  mounted() {
    document.body.classList.toggle("bg-body-secondary", !isStorefrontPath(window.location.pathname));
    this.syncDarkMode();
    window.addEventListener("mobile-chat-open-change", this.onMobileChatOpenChange);
    window.addEventListener("billingRestrictionMessageChanged", this.updateBillingRestrictionMessage);
    if (!isStorefrontPath(window.location.pathname)) this.setConfiguration();
  },
  unmounted() {
    window.removeEventListener("mobile-chat-open-change", this.onMobileChatOpenChange);
    window.removeEventListener("billingRestrictionMessageChanged", this.updateBillingRestrictionMessage);
  },
  setup() {
    const route = useRoute();
    const stateStoreInstance = stateStore;
    
    const currentRoute = computed(() => route.path);
    const isStorefront = computed(() => route.meta.storefront === true);

    watchEffect(() => {
      if (isStorefront.value) {
        document.body.classList.remove('sidebar-show', 'sidebar-hide');
        return;
      }
      if (stateStoreInstance.open) {
        document.body.classList.remove("sidebar-show");
        document.body.classList.add("sidebar-hide");
      } else {
        document.body.classList.remove("sidebar-hide");
        document.body.classList.add("sidebar-show");
      }
    });

    return {
      currentRoute,
      isStorefront
    };
  },
});
</script>
<style scoped>
.main-content.onboarding-layout {
  margin: 0 !important;
  padding: max(16px, env(safe-area-inset-top)) 16px 24px !important;
  width: 100%;
  min-height: 100svh;
  justify-content: flex-start;
  background: #f6faf8;
}
.validate-home-page{
  padding: unset !important;
}

.billing-restriction-modal {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 16px;
  position: fixed;
  z-index: 1055;
}

.billing-restriction-modal__backdrop {
  background-color: rgba(17, 24, 39, 0.45);
  inset: 0;
  position: absolute;
}

.billing-restriction-modal__content {
  background-color: #ffffff;
  border-radius: 7px;
  box-shadow: 0 20px 60px rgba(17, 24, 39, 0.18);
  max-width: 430px;
  padding: 28px;
  position: relative;
  text-align: center;
  width: 100%;
  z-index: 1;
}

.billing-restriction-modal__close {
  align-items: center;
  background-color: transparent;
  border: 0;
  color: #8f9bb3;
  display: flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 14px;
  top: 14px;
  width: 32px;
}

.billing-restriction-modal__icon {
  align-items: center;
  background-color: rgba(255, 193, 7, 0.16);
  border-radius: 50%;
  color: #8a6400;
  display: flex;
  font-size: 28px;
  height: 58px;
  justify-content: center;
  margin: 0 auto 18px;
  width: 58px;
}

.billing-restriction-modal__title {
  color: #111827;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 10px;
}

.billing-restriction-modal__message {
  color: #656f7d;
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 22px;
}

@media (min-width: 1200px) {
  .main-content.attendants-day-main {
    min-height: 100vh;
    padding-left: 64px !important;
    padding-right: 0 !important;
    padding-top: 0 !important;
  }
}

/* When on the Attendants Day page, remove the main-content padding on mobile so the schedule fills the screen. */
@media (max-width: 767px) {
  :deep(.header-burger-menu) {
    display: none !important;
  }

  :deep(.header-area) {
    display: none !important;
  }

  .main-content.with-mobile-bottom-nav {
    padding-top: 24px !important;
    padding-bottom: max(96px, calc(82px + env(safe-area-inset-bottom))) !important;
  }

  .main-content.chat-main.with-mobile-bottom-nav {
    height: 100vh;
    height: 100dvh;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: hidden !important;
    padding-top: 0 !important;
    padding-bottom: max(74px, calc(62px + env(safe-area-inset-bottom))) !important;
  }

  .main-content.chat-main.mobile-chat-open.with-mobile-bottom-nav {
    padding-bottom: 0 !important;
  }

  .main-content.attendants-day-main {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .main-content.attendants-day-main.with-mobile-bottom-nav {
    height: 100vh;
    height: 100dvh;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: hidden !important;
    padding-top: 10px !important;
    padding-bottom: max(74px, calc(62px + env(safe-area-inset-bottom))) !important;
  }
}
</style>
